/**
 * "Won — existing customer…" on a lead, with `v1_completion` on (v1 completion
 * C5f, Ben's d84; mock-up state 9): the won lead is a customer CIS already
 * has. The rep chooses it from their own customer search and the lead moves
 * to First order, linked to it. No customer is created.
 */
import { useEffect, useId, useState } from "react";
import { Loader2 } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { LeadDetail, LeadListItem } from "@shared-core/data/api-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  EntityCombobox,
  type EntityOption,
} from "../../patterns/EntityCombobox";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";

const MATCH_LIMIT = 10;

export function LeadLinkExistingCustomerDialog({
  lead,
  open,
  onOpenChange,
  onConverted,
}: {
  lead: Pick<LeadDetail, "id" | "company_name">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConverted: (lead: LeadListItem) => void;
}) {
  const adapter = useDataAdapter();
  const idPrefix = useId();
  const [customer, setCustomer] = useState<EntityOption | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCustomer(null);
    setFieldError(null);
    setFailure(null);
  }, [open]);

  const link = async () => {
    if (!customer) {
      setFieldError("Choose a customer from the list.");
      return;
    }
    setSaving(true);
    setFieldError(null);
    setFailure(null);
    try {
      onConverted(
        await adapter.convertLeadToExistingCustomer(lead.id, customer.id),
      );
    } catch (err) {
      const errors = extractFieldErrors(err);
      if (errors.length > 0) {
        setFieldError(errors.map((error) => error.message).join(" "));
      } else {
        setFailure(
          mutationErrorMessage(err, "The lead wasn't linked. Try again."),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const errorId = `${idPrefix}-customer-error`;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Won — existing customer</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void link();
          }}
        >
          {failure && (
            <p
              role="alert"
              className="rounded-md border border-red-300 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:text-red-400"
            >
              {failure}
            </p>
          )}
          <div className="space-y-1">
            <span className="text-sm font-medium">Customer</span>
            <EntityCombobox
              value={customer}
              onChange={(option) => {
                setCustomer(option);
                setFieldError(null);
              }}
              fetchOptions={async (q) => {
                const page = await adapter.listCisCustomers({
                  q: q || lead.company_name,
                  pageSize: MATCH_LIMIT,
                  isActive: true,
                });
                return page.items.map((c) => ({
                  id: c.customer_code,
                  label: c.name,
                  hint: c.tharstern_customer_code
                    ? `${c.customer_code} · ${c.tharstern_customer_code}`
                    : c.customer_code,
                }));
              }}
              placeholder="Search by name or code…"
              ariaLabel="Customer"
              aria-invalid={fieldError ? true : undefined}
              aria-describedby={fieldError ? errorId : undefined}
              disabled={saving}
            />
            {fieldError && (
              <p
                id={errorId}
                className="text-sm text-red-600 dark:text-red-400"
              >
                {fieldError}
              </p>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            This marks the lead as won and links it to the customer you choose.
            The lead moves to First order, leaves the open pipeline and
            can&apos;t be moved back. No new customer is created.
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="size-3.5 animate-spin" />}
              Link to customer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
